// ============================================================
//  ALTIMO Medical Center — Enfermeria.jsx
//  Módulo de Enfermería
//  Formularios: Signos vitales, Reporte enfermería, Temp/Pulso,
//               Balance hídrico, Tarjetas de medicamentos
//  v6: + Devolución de medicamentos a Farmacia
//      (la enfermera solicita; Farmacia confirma o rechaza —
//       stock/cuenta solo se mueven al confirmar)
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import Formularios from './Formularios';
import { PanelEstacionEnfermeria } from './EstacionEnfermeria';

const C = {
  azul:   '#0f766e',
  azulOsc:'#134e4a',
  verde:  '#1D9E75',
  rojo:   '#dc3545',
  naranja:'#e67e22',
  morado: '#7c3aed',
  gris:   '#6c757d',
};

const S = {
  card:   { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', margin:'0 24px 14px' },
  titulo: { fontSize:'12px', fontWeight:'700', color: C.azulOsc, textTransform:'uppercase', letterSpacing:'.05em',
            marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f4f3',
            display:'flex', justifyContent:'space-between', alignItems:'center' },
  input:  { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda', borderRadius:'7px',
            background:'#fff', outline:'none', boxSizing:'border-box' },
  label:  { display:'block', fontSize:'11px', fontWeight:'600', color: C.gris, marginBottom:'4px',
            textTransform:'uppercase', letterSpacing:'.04em' },
  campo:  { marginBottom:'12px' },
  btnPrimario: { background: C.azul, color:'#fff', border:'none', borderRadius:'7px', padding:'8px 18px',
                 fontSize:'13px', fontWeight:'700', cursor:'pointer' },
  btnSecundario: { background:'#fff', color:'#495057', border:'1px solid #c0ccda', borderRadius:'7px',
                   padding:'7px 14px', fontSize:'12px', cursor:'pointer' },
  pill: (color) => ({
    display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: { verde:'#d4edda', amber:'#fff3cd', azul:'#cce5ff', rojo:'#f8d7da', gris:'#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde:'#155724', amber:'#856404', azul:'#004085', rojo:'#721c24', gris:'#383d41' }[color] || '#333',
  }),
};

const ESTADO_PRESC = {
  Pendiente:            { color:'amber', label:'⏳ Pendiente de dispensar' },
  Dispensado:           { color:'verde', label:'✔ Dispensado — puede devolverse' },
  Devolucion_Pendiente: { color:'azul',  label:'↩ Devolución en revisión por Farmacia' },
  Devuelto_Parcial:     { color:'azul',  label:'↩ Devuelto parcial — puede devolver más' },
  Devuelto_Total:       { color:'gris',  label:'↩ Devuelto en su totalidad' },
  Cancelado:            { color:'rojo',  label:'✕ Cancelado' },
};

// ── Panel: la enfermera busca al paciente y solicita devoluciones ──
function PanelDevolucionMedicamentos({ usuario }) {
  const [busqueda,  setBusqueda]  = useState('');
  const [resultados,setResultados]= useState([]);
  const [admision,  setAdmision]  = useState(null);
  const [prescripciones, setPrescripciones] = useState([]);
  const [cargando,  setCargando]  = useState(false);
  const [modal,      setModal]    = useState(null); // prescripción seleccionada para devolver

  // Búsqueda de paciente con admisión activa
  useEffect(() => {
    if (busqueda.trim().length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.admisiones.buscar(busqueda);
        const lista = (r?.data ?? r ?? []).filter(a => String(a.estado||'').toLowerCase() === 'activa');
        setResultados(lista);
      } catch(_) { setResultados([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const cargarPrescripciones = useCallback(async (admision_id) => {
    setCargando(true);
    try {
      const r = await api.prescripciones.listar({ admision_id });
      setPrescripciones(r?.ok ? r.data : []);
    } catch(_) { setPrescripciones([]); }
    setCargando(false);
  }, []);

  const seleccionarPaciente = (a) => {
    setAdmision(a); setBusqueda(''); setResultados([]);
    cargarPrescripciones(a.id);
  };

  // Solo se puede devolver lo dispensado y no devuelto todavía
  const puedeDevolver = (p) => ['Dispensado', 'Devuelto_Parcial'].includes(p.estado)
    && Number(p.cantidad_dispensada) - Number(p.cantidad_devuelta) > 0;

  return (
    <div>
      <div style={S.card}>
        <div style={S.titulo}><span>🔍 Buscar paciente hospitalizado</span></div>
        {!admision ? (
          <div style={{ position:'relative' }}>
            <input style={S.input} placeholder="Nombre, DUI o número de habitación/admisión..."
              value={busqueda} onChange={e => setBusqueda(e.target.value)} autoFocus />
            {resultados.length > 0 && (
              <div style={{ border:'1px solid #dde3ea', borderRadius:'8px', marginTop:'6px', maxHeight:'220px', overflowY:'auto' }}>
                {resultados.map(a => (
                  <div key={a.id} onClick={() => seleccionarPaciente(a)}
                    style={{ padding:'10px 12px', cursor:'pointer', borderBottom:'1px solid #eef1f6',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f7f5'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:'13px', color: C.azulOsc }}>{a.paciente}</div>
                      <div style={{ fontSize:'11px', color: C.gris }}>
                        {a.habitacion ? `Hab. ${a.habitacion} · ` : ''}{a.medico || ''} · Adm. #{a.id}
                      </div>
                    </div>
                    <span style={S.pill('verde')}>Activa</span>
                  </div>
                ))}
              </div>
            )}
            {busqueda.trim().length >= 2 && resultados.length === 0 && (
              <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'6px' }}>
                Sin resultados de admisión activa.
              </div>
            )}
          </div>
        ) : (
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontWeight:800, fontSize:'15px', color: C.azulOsc }}>{admision.paciente}</div>
              <div style={{ fontSize:'12px', color: C.gris, marginTop:'2px' }}>
                {admision.habitacion ? `Hab. ${admision.habitacion} · ` : ''}{admision.medico || ''} · Admisión #{admision.id}
              </div>
            </div>
            <button style={S.btnSecundario} onClick={() => { setAdmision(null); setPrescripciones([]); }}>
              Cambiar paciente
            </button>
          </div>
        )}
      </div>

      {admision && (
        <div style={S.card}>
          <div style={S.titulo}>
            <span>💊 Medicamentos dispensados a este paciente</span>
            <button style={S.btnSecundario} onClick={() => cargarPrescripciones(admision.id)}>Actualizar</button>
          </div>

          {cargando ? (
            <div style={{ textAlign:'center', padding:'20px', color: C.gris }}>Cargando...</div>
          ) : prescripciones.length === 0 ? (
            <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
              No hay prescripciones registradas para este paciente.
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
              <thead>
                <tr>
                  {['Medicamento','Recetado','Dispensado','Devuelto','Disponible p/devolver','Estado',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'8px 10px', fontSize:'11px', color: C.gris,
                      fontWeight:'600', borderBottom:'2px solid #e8f4f3', background:'#f8fdfc' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prescripciones.map(p => {
                  const est = ESTADO_PRESC[p.estado] || { color:'gris', label:p.estado };
                  const disponible = Number(p.cantidad_dispensada) - Number(p.cantidad_devuelta);
                  return (
                    <tr key={p.id}>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                        {p.medicamento}
                        {p.es_controlado === 1 && <span style={{ color: C.morado, marginLeft:'4px' }}>🔒</span>}
                      </td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', textAlign:'center' }}>{p.cantidad_recetada}</td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', textAlign:'center' }}>{p.cantidad_dispensada || 0}</td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', textAlign:'center' }}>{p.cantidad_devuelta || 0}</td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', textAlign:'center', fontWeight:700 }}>
                        {disponible > 0 ? disponible : '—'}
                      </td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                        <span style={S.pill(est.color)}>{est.label}</span>
                      </td>
                      <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                        {puedeDevolver(p) && (
                          <button style={{ ...S.btnPrimario, fontSize:'11px', padding:'5px 12px' }}
                            onClick={() => setModal(p)}>
                            ↩ Devolver
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <ModalSolicitarDevolucion
          presc={modal}
          usuario={usuario}
          onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); cargarPrescripciones(admision.id); }}
        />
      )}
    </div>
  );
}

// ── Modal: enfermera solicita la devolución (paso 1, no toca stock) ──
function ModalSolicitarDevolucion({ presc, usuario, onCerrar, onListo }) {
  const disponible = Number(presc.cantidad_dispensada) - Number(presc.cantidad_devuelta);
  const [cantidad,  setCantidad]  = useState(String(disponible));
  const [motivo,    setMotivo]    = useState('');
  const [procesando,setProcesando]= useState(false);
  const [err,       setErr]       = useState('');

  const confirmar = async () => {
    setErr('');
    const cant = Number(cantidad);
    if (!cant || cant <= 0)       { setErr('Ingresa una cantidad válida.'); return; }
    if (cant > disponible)        { setErr(`Máximo disponible para devolver: ${disponible}.`); return; }
    if (!motivo.trim())           { setErr('Indica el motivo de la devolución.'); return; }
    setProcesando(true);
    try {
      const r = await api.devoluciones.solicitar({
        prescripcion_id: presc.id, cantidad_solicitada: cant,
        motivo_enfermera: motivo.trim(), usuario_id: usuario.id,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo registrar la solicitud.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,30,30,.75)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'460px', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azul, padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>↩ Solicitar devolución a Farmacia</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            Esta solicitud queda pendiente hasta que Farmacia confirme la recepción física.
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc }}>{presc.medicamento}</div>
            <div style={{ fontSize:'11px', color: C.gris, marginTop:'2px' }}>
              Dispensado: {presc.cantidad_dispensada} {presc.unidad} · Disponible para devolver: {disponible} {presc.unidad}
            </div>
          </div>

          <label style={S.label}>Cantidad a devolver *</label>
          <input style={S.input} type="number" min="0.01" max={disponible} step="any"
            value={cantidad} onChange={e => setCantidad(e.target.value)} />

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Motivo *</label>
            <input style={S.input} placeholder="Ej: alta médica, cambio de receta, no se administró..."
              value={motivo} onChange={e => setMotivo(e.target.value)} />
          </div>

          <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'10px' }}>
            El medicamento físico debe entregarse a Farmacia. El stock, el kardex y la cuenta del
            paciente solo se actualizarán cuando Farmacia confirme la recepción.
          </div>

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btnSecundario, flex:1, textAlign:'center' }} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btnPrimario, flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Enviando...' : '✔ Enviar solicitud'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Panel: lista global de indicaciones pendientes/notificadas/vencidas
//    de todas las admisiones activas, con botón para marcar cumplida ──
function PanelIndicacionesPendientes({ usuario, filtroInicial }) {
  const [indicaciones, setIndicaciones] = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [modal,        setModal]        = useState(null); // indicación seleccionada para cumplir
  const [filtroEstado, setFiltroEstado] = useState(filtroInicial || 'Todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.indicacionesEnf.pendientes();
      setIndicaciones(r?.ok ? r.data : []);
    } catch(_) { setIndicaciones([]); }
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
    // Refresca junto con el resto de notificaciones de la app (cada 15s).
    const t = setInterval(cargar, 15000);
    return () => clearInterval(t);
  }, [cargar]);

  const fmtFechaHora = (s) => {
    if (!s) return '—';
    try { return new Date(String(s).replace(' ', 'T')).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' }); }
    catch (_) { return s; }
  };

  const minutosRestantes = (proxima) => {
    const ms = new Date(String(proxima).replace(' ', 'T')) - new Date();
    return Math.round(ms / 60000);
  };

  const ESTADO_IND = {
    Pendiente:  { color:'azul',  label:'🕐 Programada' },
    Notificada: { color:'amber', label:'🔔 Aviso enviado' },
    Vencida:    { color:'rojo',  label:'⚠ Vencida' },
  };

  const indicacionesFiltradas = indicaciones.filter(ind => {
    if (filtroEstado === 'Todos') return true;
    if (filtroEstado === 'Vencida') return ind.estado === 'Vencida';
    if (filtroEstado === 'Proximas') return ind.estado !== 'Vencida';
    return true;
  });

  return (
    <div style={S.card}>
      <div style={S.titulo}>
        <span>🔔 Indicaciones médicas pendientes ({indicacionesFiltradas.length})</span>
        <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
          {[
            { id:'Todos',    label:'Todos' },
            { id:'Vencida',  label:'⚠ Vencidas' },
            { id:'Proximas', label:'🔔 Próximas' },
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroEstado(f.id)}
              style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'600',
                cursor:'pointer', border:'none',
                background: filtroEstado === f.id ? C.azulOsc : '#e8f4f3',
                color: filtroEstado === f.id ? '#fff' : C.azulOsc }}>
              {f.label}
            </button>
          ))}
          <button style={S.btnSecundario} onClick={cargar}>Actualizar</button>
        </div>
      </div>

      {cargando ? (
        <div style={{ textAlign:'center', padding:'20px', color: C.gris }}>Cargando...</div>
      ) : indicacionesFiltradas.length === 0 ? (
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          No hay indicaciones pendientes de cumplir. ✔
        </div>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
          <thead>
            <tr>
              {['Paciente','Hab.','Indicación','Programada','Faltan','Estado','Médico',''].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'8px 10px', fontSize:'11px', color: C.gris,
                  fontWeight:'600', borderBottom:'2px solid #e8f4f3', background:'#f8fdfc' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicacionesFiltradas.map(ind => {
              const est  = ESTADO_IND[ind.estado] || { color:'gris', label:ind.estado };
              const mins = minutosRestantes(ind.proxima_ejecucion);
              const faltan = ind.estado === 'Vencida'
                ? <span style={{ color: C.rojo, fontWeight:700 }}>Vencida</span>
                : mins <= 0
                  ? <span style={{ color: C.naranja, fontWeight:700 }}>Ahora</span>
                  : `${mins} min`;
              return (
                <tr key={ind.id} style={ind.estado === 'Vencida' ? { background:'#fff5f5' } : undefined}>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontWeight:600 }}>{ind.paciente}</td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>{ind.habitacion || '—'}</td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>{ind.descripcion}</td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontSize:'12px', whiteSpace:'nowrap' }}>
                    {fmtFechaHora(ind.proxima_ejecucion)}
                  </td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>{faltan}</td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                    <span style={S.pill(est.color)}>{est.label}</span>
                  </td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontSize:'12px' }}>
                    {ind.medico_nombre ? `${ind.medico_nombre} ${ind.medico_apellido||''}` : '—'}
                  </td>
                  <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                    <button style={{ ...S.btnPrimario, fontSize:'11px', padding:'5px 12px' }}
                      onClick={() => setModal(ind)}>
                      ✔ Cumplir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <ModalCumplirIndicacion
          indicacion={modal}
          usuario={usuario}
          onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); cargar(); }}
        />
      )}
    </div>
  );
}

// ── Modal: la enfermera confirma cumplimiento (firma con usuario + hora) ──
function ModalCumplirIndicacion({ indicacion, usuario, onCerrar, onListo }) {
  const [observacion, setObservacion] = useState('');
  const [procesando,  setProcesando]  = useState(false);
  const [err,         setErr]         = useState('');

  const confirmar = async () => {
    setErr('');
    setProcesando(true);
    try {
      const r = await api.indicacionesEnf.cumplir({
        id: indicacion.id, usuario_id: usuario.id, observacion: observacion.trim() || null,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo registrar el cumplimiento.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,30,30,.75)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'460px', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azul, padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>✔ Confirmar cumplimiento</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            Quedará registrado con tu nombre y la hora exacta para trazabilidad.
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azulOsc }}>{indicacion.paciente}</div>
            <div style={{ fontSize:'12px', color: C.gris, marginTop:'4px' }}>{indicacion.descripcion}</div>
          </div>

          <label style={S.label}>Observación (opcional)</label>
          <textarea style={{ ...S.input, resize:'vertical', minHeight:'60px' }}
            placeholder="Ej: tolerado sin novedad, paciente refiere..."
            value={observacion} onChange={e => setObservacion(e.target.value)} />

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btnSecundario, flex:1, textAlign:'center' }} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btnPrimario, flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Guardando...' : '✔ Marcar como cumplida'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id:'formularios',   label:'📋 Formularios' },
  { id:'indicaciones',  label:'🔔 Indicaciones pendientes' },
  { id:'estacion',      label:'🏥 Estación' },
  { id:'devoluciones',  label:'↩ Devolución de medicamentos' },
];

export default function Enfermeria({ usuario, filtroInicial }) {
  const [tab, setTab] = useState(filtroInicial ? 'indicaciones' : 'formularios');

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* Header módulo */}
      <div style={{ background:'linear-gradient(135deg,#0f766e,#134e4a)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          🏥 Módulo de Enfermería
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'12px' }}>
          <span>📊 Signos vitales</span>
          <span>📋 Reporte de enfermería</span>
          <span>🌡 Temperatura y pulso</span>
          <span>💧 Balance hídrico</span>
          <span>💊 Tarjetas de medicamentos</span>
          <span>🔔 Indicaciones con recordatorio</span>
        </div>
        {/* Tabs */}
        <div style={{ display:'flex', gap:'2px' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit', transition:'all .15s',
                background: tab === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      tab === t.id ? C.azulOsc : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'formularios' && (
        <Formularios usuario={usuario} areaFiltro="enfermeria" />
      )}
      {tab === 'indicaciones' && (
        <div style={{ background:'#f0f4f8', minHeight:'100vh', paddingTop:'20px' }}>
          <PanelIndicacionesPendientes usuario={usuario} filtroInicial={filtroInicial} />
        </div>
      )}
      {tab === 'estacion' && (
        <div style={{ background:'#f0f4f8', minHeight:'100vh', paddingTop:'20px' }}>
          <PanelEstacionEnfermeria usuario={usuario} />
        </div>
      )}
      {tab === 'devoluciones' && (
        <div style={{ background:'#f0f4f8', minHeight:'100vh', paddingTop:'20px' }}>
          <PanelDevolucionMedicamentos usuario={usuario} />
        </div>
      )}
    </div>
  );
}
